import {
  DRAWER_TYPE,
  TEST_RECOMMENDATIONS_DRAWER,
  TABLE_ASSET_ID,
  TEST_ID,
  CHAT_INCIDENT_ID,
} from '../../constants/urlParams';
import { timeConsts } from '../../utils/datetime';
import type { SessionStatus } from '../../types';
import {
  AiAgent,
  Announcement,
  ChatMode,
  FETool,
  ModelProvider,
  NavigationSchemaToolItem,
  Suggestion,
} from './types';

export const HISTORY_SINCE_MILLISECONDS = 3 * timeConsts.month * 60 * 1000;
export const HISTORY_PAGE_SIZE = 50;
export const SIDE_PANEL_WIDTH = 360;

export const SESSION_ACTIVE_STATUSES: SessionStatus[] = [
  'queued',
  'running',
  'waiting_for_input',
];

export const CHAT_MODES: { key: ChatMode; label: string }[] = [
  { key: 'agent', label: 'Edit' },
  { key: 'ask', label: 'Ask' },
];

export const SHOW_ANNOUNCEMENTS_ONLY_IN_EMPTY_STATE = true;

export const ANNOUNCEMENTS: Announcement[] = [
  {
    prefix: 'New',
    message: 'Manage cloud tests in bulk.',
  },
];

// AWS Bedrock models available in the prod region (eu-central-1).
export const AI_MODELS: ModelProvider[] = [
  {
    type: 'bedrock',
    modelName: 'eu.anthropic.claude-opus-4-8',
    label: 'Opus 4.8',
    description: 'Best for everyday, complex tasks',
  },
  {
    type: 'bedrock',
    modelName: 'eu.anthropic.claude-sonnet-4-6',
    label: 'Sonnet 4.6',
    description: 'Efficient for routine tasks',
  },
  {
    type: 'bedrock',
    modelName: 'eu.anthropic.claude-haiku-4-5-20251001-v1:0',
    label: 'Haiku 4.5',
    description: 'Fastest for quick answers',
  },
];

export const DEFAULT_MODEL: ModelProvider = AI_MODELS[0];

export const AI_AGENT: AiAgent = {
  label: 'AI Agent',
  urlPath: '/ai/agent',
  placeholder: 'Ask Elementary to build, fix, or explore',
};

export type RouteChatConfig = {
  suggestions: Suggestion[];
};

export const filterSuggestions = (
  suggestions: Suggestion[],
  incidentsV2Enabled: boolean,
): Suggestion[] =>
  suggestions.filter(
    (s) =>
      typeof s === 'string' || !s.requiresIncidentsV2 || incidentsV2Enabled,
  );

export const FALLBACK_CHAT_CONFIG: RouteChatConfig = {
  suggestions: [
    'Show me my data health overview',
    'Are there any new open incidents?',
    'Which models need better test coverage?',
  ],
};

export const ROUTE_CHAT_CONFIGS: {
  pattern: string;
  config: RouteChatConfig;
}[] = [
  {
    pattern: '/catalog',
    config: {
      suggestions: [
        'Find a data asset',
        "Find assets that haven't been updated",
        'Find assets with open incidents',
        'Suggest tests for my assets',
      ],
    },
  },
  {
    pattern: '/incidents',
    config: {
      suggestions: [
        {
          label: 'Investigate and group incidents by root cause',
          requiresIncidentsV2: true,
        },
        {
          label: 'Classify and assign incidents',
          requiresIncidentsV2: true,
        },
        'Summarize open incidents',
        'Analyze the most impactful incidents',
      ],
    },
  },
  {
    pattern: '/test-management',
    config: {
      suggestions: [
        'Add tests to assets',
        'Edit existing tests',
        'Remove existing tests',
        'Find and fix coverage gaps',
        { label: 'Apply a test across multiple assets', isNew: true },
      ],
    },
  },
  {
    pattern: '/report/model-runs',
    config: {
      suggestions: [
        'Which models are slowest?',
        'Show me models with increasing run times',
        'Compare execution times across environments',
      ],
    },
  },
  {
    pattern: '/report/dashboard',
    config: {
      suggestions: [
        'Find assets with critical issues',
        'Summarize incidents from the past week',
        'Find incidents hitting critical assets',
      ],
    },
  },
  {
    pattern: '/data-health',
    config: {
      suggestions: [
        'Break down health by tag',
        'Show health for critical assets',
        'Break down health by owner',
      ],
    },
  },
  {
    pattern: '/report/lineage',
    config: {
      suggestions: [
        'Which assets depend on this source?',
        'Are there any test failures in this path?',
      ],
    },
  },
];

export const APP_NAVIGATION_SCHEMA: NavigationSchemaToolItem[] = [
  {
    name: 'Dashboard',
    path: '/report/dashboard',
    description:
      'Main overview page providing high-level data health metrics and status across your data pipeline.',
  },
  {
    name: 'Data Health',
    path: '/data-health',
    description:
      'Displays data health scores across 6 quality dimensions (completeness, accuracy, validity, uniqueness, consistency, freshness) with configurable thresholds and color coding for stakeholders.',
  },
  {
    name: 'Catalog',
    path: '/catalog',
    description:
      'Dynamic workspace for managing data assets (models, sources, columns, tests, metrics) with metadata, ownership, and collaboration features. Supports editing metadata and bulk operations.',
  },
  {
    name: 'Incidents',
    path: '/incidents',
    description:
      'Centralized incident management page showing all current and historical data issues with status, assignee, severity, and resolution tracking. Enables collaboration on resolving data problems.',
  },
  {
    name: 'Lineage',
    path: '/report/lineage',
    description:
      'End-to-end column-level data lineage visualization showing dependencies from data warehouse to BI dashboards, with test results and impact analysis capabilities.',
  },
  {
    name: 'Metrics',
    path: '/metrics',
    description:
      'Displays collected data content metrics over time with anomaly detection charts. Shows metrics like row counts, null counts, and custom business metrics.',
  },
  {
    name: 'Test Results',
    path: '/report/test-results',
    description:
      'Shows execution results of all data quality tests including dbt tests, anomaly detection tests, and Elementary monitors with pass/fail/warning status.',
  },
  {
    name: 'Test Configuration',
    path: '/test-management',
    description:
      'UI for configuring data quality tests directly in Elementary Cloud, which creates pull requests to your code repository with the test definitions.',
    envPermission: 'EDIT_TEST_CONFIGURATIONS',
  },
  {
    name: 'Model Performance',
    path: '/report/model-runs',
    description:
      'Monitors execution times of dbt models showing latest duration, median duration, and trends to identify performance bottlenecks.',
  },
  {
    name: 'Test Performance',
    path: '/report/test-runs',
    description:
      'Displays test execution history with median execution times and fail rates to identify compute-heavy tests.',
  },
  {
    name: 'Environments',
    path: '/settings/environments',
    accountScopeRoute: true,
    description:
      'Manages different Elementary environments and their integrations with data warehouses, BI tools, and alert destinations.',
    accountPermission: 'EDIT_INTEGRATIONS',
  },
  {
    name: 'Alert Rules',
    path: '/alert-rules',
    description:
      'Configures routing rules for alerts, defining which alerts go to which destinations (Slack, Teams, PagerDuty) based on filters like asset type, owner, or downstream impact.',
  },
  {
    name: 'Pull Requests',
    path: '/pull-requests',
    description:
      "Shows pull requests created by Elementary's AI agents and configuration changes for review and approval.",
  },
  // {
  //   name: 'Catalog AI Assistant',
  //   path: '/catalog',
  //   description:
  //     'AI agent that helps users find relevant data assets using natural language queries, providing trust signals and data health context.',

  // },
  {
    name: 'Test Recommendations AI Agent',
    path: `/catalog?${DRAWER_TYPE}=asset&${TEST_RECOMMENDATIONS_DRAWER}=true`,
    urlParams: [TABLE_ASSET_ID],
    description:
      'Analyzes data assets and suggests high-impact tests to improve coverage while reducing alert fatigue.',
  },
  {
    name: 'Test Overview',
    path: `/report/test-results?${DRAWER_TYPE}=test`,
    urlParams: [TEST_ID],
    description:
      'Detailed view of individual test configurations, execution history, and results with upstream/downstream dependencies.',
  },
  {
    name: 'Incident Investigation AI Agent',
    path: '/incidents',
    urlParams: [CHAT_INCIDENT_ID],
    description:
      'Automates root cause analysis for data incidents, analyzes impact, and suggests fixes with pull request automation.',
  },
];

const getAppNavigationSchema = () => {
  const createPageSchema = (navigationItem: NavigationSchemaToolItem) => {
    const urlParameterProperties: Record<string, { type: 'string' }> = {};
    const urlParams = navigationItem.urlParams || [];

    urlParams.forEach((param) => {
      urlParameterProperties[param] = { type: 'string' as const };
    });

    const requiredFields = ['name', ...urlParams];

    return {
      type: 'object' as const,
      properties: {
        name: { type: 'string' as const, const: navigationItem.name },
        ...urlParameterProperties,
      },
      required: requiredFields,
    };
  };

  const allPageSchemas = APP_NAVIGATION_SCHEMA.map(createPageSchema);

  return {
    type: 'object' as const,
    properties: {
      page: {
        oneOf: allPageSchemas,
      },
    },
    required: ['page'] as const,
  };
};

const getAppNavigationDescriptions = () =>
  APP_NAVIGATION_SCHEMA.map(
    (item) => `${item.name} - ${item.description}`,
  ).join('\n');

export const FE_TOOLS: FETool[] = [
  {
    name: 'lineage_graph_display',
    description: `
    When presenting asset lineage or dependency relationships:
    - ALWAYS use this tool when displaying upstream or downstream dependencies. Never describe lineage with plain text lists or markdown.
    - Never duplicate graph data by describing it in text after using this tool.
    - After using this tool, refer to the graph naturally without recreating it.
    - Format nodes as: { id: string, label: string, type: "TABLE" | "EXPOSURE" | "SOURCE", layer: number }
      - layer 0 = the central/focus asset
      - negative integers (−1, −2, …) = upstream assets (things the focus depends on)
      - positive integers (1, 2, …) = downstream assets (things that depend on the focus)
    - Format edges as: { source: string, target: string } where source is the upstream node id
    - Hard limits (enforced by the tool): max 10 nodes total, max 5 distinct layers
    - If the full lineage exceeds these limits, include the most impactful/direct assets and briefly note the truncation.`,
    argsSchema: {
      type: 'object',
      properties: {
        nodes: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string' },
              label: { type: 'string' },
              type: { type: 'string' },
              layer: { type: 'number' },
            },
            required: ['id', 'label'],
          },
        },
        edges: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              source: { type: 'string' },
              target: { type: 'string' },
            },
            required: ['source', 'target'],
          },
        },
      },
      required: ['nodes', 'edges'],
    },
    hasOutput: false,
  },
  {
    name: 'user_actions_display',
    description: `
    - Always use this tool when you want to display actions or options for the user to choose from. 
    - Avoid suggesting options to navigate to previous states in the conversation, end conversation, 
      or things of that nature, for example: "back to main menu", "exit conversation".
    - Important: never suggest to navigate to the different pages of the app as actions in this tool, we have a dedicated tool for that.
    - The actions text should be concise and short, since they display inside buttons.`,
    argsSchema: {
      type: 'object',
      properties: {
        actions: { type: 'array', items: { type: 'string' } },
      },
      required: ['actions'],
    },
    hasOutput: true,
  },
  {
    name: 'app_navigation',
    description: `
    - Always use this tool whenever you need to guide the user to a specific page or view in the app.
    - The tool will generate a clickable link labeled with the page name. When clicked, it will navigate the user directly to the correct page.
    - Important: After using this tool, do NOT include any additional link-like text or references in your response. The tool creates the functional link automatically.
    - Never include placeholder links like "javascript:void(0)" or any other non-functional link text in your response.
    - Simply use the tool and then refer to the created link naturally (e.g., "You can access this via the link above" or "The link will take you to the relevant page").
    - Be sure to introduce the link naturally in your response by prefixing it with appropriate context (e.g., “You can view this here:” or “Navigate to this page:”).
    - Here are descriptions of all the pages/views so you'll know which to use based on the conversation context:
      ${getAppNavigationDescriptions()}`,
    argsSchema: getAppNavigationSchema(),
    hasOutput: false,
    enabledModes: [],
  },
];
